import type { CSSProperties } from "react";

import { cn } from "../foundations/cn.js";
import { useThemeSpec } from "../foundations/themeContext.js";
import { WATER_LAYOUTS, type WaterLayout } from "./waterLayouts.js";

export interface StageProps {
  /** Overrides the theme's recipe, e.g. "none" to keep one section plain. */
  recipe?: "none" | "glow" | "water" | "aurora";
  className?: string;
}

// Complete names: Tailwind only emits the theme variables it finds written out in full.
const STAGE_COLORS = [
  "var(--color-stage-1)",
  "var(--color-stage-2)",
  "var(--color-stage-3)",
  "var(--color-stage-4)",
] as const;

const tint = (stage: 1 | 2 | 3 | 4, strength: number) =>
  `color-mix(in oklab, ${STAGE_COLORS[stage - 1]} ${strength}%, transparent)`;

/** One drawn variant, scaled to the stage: positions in percent, sizes and blurs in cqw. */
function WaterLight({ layout }: { layout: WaterLayout }) {
  const { w: W, h: H } = layout.frame;
  const x = (px: number) => `${(px / W) * 100}%`;
  const y = (px: number) => `${(px / H) * 100}%`;
  const cq = (px: number) => `${(px / W) * 100}cqw`;
  // Figma's layer blur reads about twice as wide as a CSS blur of the same number.
  const blur = (px: number) => `blur(${cq(px / 2)})`;
  const ellipse = ([ex, ey, ew, eh, b]: readonly number[], color: string): CSSProperties => ({
    left: x(ex ?? 0),
    top: y(ey ?? 0),
    width: x(ew ?? 0),
    height: y(eh ?? 0),
    background: color,
    filter: blur(b ?? 0),
  });
  return (
    <>
      <div className="absolute rounded-pill" style={ellipse(layout.deep, tint(1, 45))} />
      <div className="absolute rounded-pill" style={ellipse(layout.aqua, tint(2, 35))} />
      <div className="absolute rounded-pill" style={ellipse(layout.seafoam, tint(3, 14))} />
      {layout.caustics.rings.map(([rx, ry, rw, rh, rotate]) => (
        <div
          key={`${rx}-${ry}`}
          className="absolute rounded-pill"
          style={{
            left: x(rx),
            top: y(ry),
            width: x(rw),
            height: y(rh),
            rotate: `${rotate}deg`,
            border: `${cq(layout.caustics.stroke)} solid ${tint(4, 6)}`,
            filter: blur(layout.caustics.blur),
          }}
        />
      ))}
      <div
        // Its own layer while it sways, so the blurred shafts inside rasterize once, not per frame.
        className="absolute inset-0 motion-safe:md:animate-stage-sway motion-safe:md:[will-change:rotate]"
        style={{ transformOrigin: `${x(layout.rays.shafts[2]?.[0] ?? W / 2)} 0` }}
      >
        {layout.rays.shafts.map(([rx, rw, rotate]) => (
          <div
            key={rx}
            className="absolute origin-top-left"
            style={{
              left: x(rx),
              top: y(layout.rays.top),
              width: x(rw),
              height: y(H),
              rotate: `${rotate}deg`,
              background: `linear-gradient(to bottom, ${tint(4, 16)}, transparent)`,
              filter: blur(layout.rays.blur),
            }}
          />
        ))}
      </div>
      {layout.bubbles.map(([bx, by, size, dot]) => (
        <span
          key={`${bx}-${by}`}
          className="absolute flex items-center justify-center rounded-pill"
          style={{
            left: x(bx),
            top: y(by),
            width: cq(size),
            height: cq(size),
            border: `1px solid ${tint(4, 40)}`,
          }}
        >
          {dot > 0 && (
            <span
              className="rounded-pill"
              style={{ width: cq(dot), height: cq(dot), background: tint(4, 40) }}
            />
          )}
        </span>
      ))}
    </>
  );
}

/**
 * The theme's atmosphere behind a hero, a box or a closing band: light falling into the room.
 * The water recipe draws three variants (hero, pack stage, mobile), chosen by the
 * stage's own size and shape (container queries in theme.css). Purely decorative and behind the
 * content. Its one ambient motion (the rays' sway) runs a few times and stops, only on wide
 * screens, and never under reduced motion.
 */
export function Stage({ recipe, className }: StageProps) {
  const spec = useThemeSpec();
  const kind = recipe ?? spec.stage.recipe;
  if (kind === "none") return null;
  return (
    <div
      aria-hidden
      className={cn(
        // A size container, so each variant scales with the stage. The light melts into the page
        // below rather than ending on the section's edge; the rays fade on their own.
        "pointer-events-none absolute inset-0 overflow-hidden [container-type:size] [mask-image:linear-gradient(to_bottom,black_85%,transparent)]",
        className,
      )}
    >
      {kind === "water" && (
        <>
          <div className="stage-variant-hero">
            <WaterLight layout={WATER_LAYOUTS.hero} />
          </div>
          <div className="stage-variant-pack">
            <WaterLight layout={WATER_LAYOUTS.pack} />
          </div>
          <div className="stage-variant-mobile">
            <WaterLight layout={WATER_LAYOUTS.mobile} />
          </div>
        </>
      )}
      {kind === "glow" && (
        <div
          className="absolute top-[-40%] left-[20%] h-[80%] w-[60%] rounded-pill"
          style={{ background: tint(1, 40), filter: "blur(6cqw)" }}
        />
      )}
      {kind === "aurora" && (
        <>
          <div
            className="absolute top-[-36%] left-[-14%] h-[86%] w-[62%] rounded-pill"
            style={{ background: tint(1, 40), filter: "blur(6cqw)" }}
          />
          <div
            className="absolute top-[-44%] left-[53%] h-[78%] w-[56%] rounded-pill"
            style={{ background: tint(2, 32), filter: "blur(6cqw)" }}
          />
          <div
            className="absolute top-[17%] left-[29%] h-[56%] w-[44%] rounded-pill"
            style={{ background: tint(3, 22), filter: "blur(6cqw)" }}
          />
        </>
      )}
    </div>
  );
}
